/* eslint-disable @typescript-eslint/naming-convention */
import { useAppSelector } from 'store/hooks';
import React from 'react';
import { Button, Grid } from '@mui/material';
import { Form } from 'formik';
import { LocationServicesCardComponent } from 'components/location/cards/LocationServicesCardComponent';
import { Location, ILocationConfig, LocationServices } from 'components/location/models/LocationModel';
import { MuiForm } from 'lib/Form';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { Row } from 'ui/Flex';
import { Panel } from 'ui/Panel';
import { locationApi } from '../LocationApi';
export interface LocationServicesCardProps {
    currentLocation: Location;
    setCurrentLocation: (location: Location) => void;
    locationConfig: ILocationConfig;
}

interface LocationServicesFormValues {
    pre_order: boolean;
    order_to_table: boolean;
    order_to_table_nfc: boolean;
    pay_at_table: boolean;
    pay_at_table_nfc: boolean;
    tab: boolean;
    checkins: boolean;
    offline_checkin: boolean;
    book_table: boolean;
    book_room: boolean;
    dine_in: boolean;
    delivery: boolean;
}

export const LocationServicesCard: React.FC<LocationServicesCardProps> = ({
    currentLocation,
    setCurrentLocation
}) => {
    const [isEditing, setIsEditing] = React.useState(false);
    const { computedSettings } = useAppSelector(state => state.settings);

    const initialValues: LocationServicesFormValues = React.useMemo(
        () => ({
            pre_order: currentLocation.services?.pre_order ?? false,
            order_to_table: currentLocation.services?.order_to_table ?? false,
            order_to_table_nfc: currentLocation.services?.order_to_table_nfc ?? false,
            pay_at_table: currentLocation.services?.pay_at_table ?? false,
            pay_at_table_nfc: currentLocation.services?.pay_at_table_nfc ?? false,
            tab: currentLocation.services?.tab ?? false,
            checkins: currentLocation.services?.checkins ?? false,
            offline_checkin: currentLocation.services?.offline_checkin ?? false,
            book_table: currentLocation.services?.book_table ?? false,
            book_room: currentLocation.services?.book_room ?? false,
            dine_in: currentLocation.services?.dine_in ?? false,
            delivery: currentLocation.services?.delivery ?? false
        }),
        [currentLocation.services]
    );

    const handleSubmit = React.useCallback(
        async (values: LocationServicesFormValues) => {
            const locationId = currentLocation._id;
            const response = await locationApi.update(locationId, {
                body: {
                    services: values as LocationServices
                }
            });
            if (response.ok) {
                setCurrentLocation(response.body);
            }
            setIsEditing(false);
        },
        [currentLocation._id, setCurrentLocation]
    );

    const toggleState = React.useCallback(() => {
        setIsEditing(currentValue => !currentValue);
    }, []);

    return (
        <Panel title="Services" showAction={!isEditing} onClick={toggleState} divider>
            {isEditing ? (
                <MuiForm<LocationServicesFormValues>
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ submitForm, isSubmitting }) => (
                        <Form>
                            <Grid container spacing={2}>
                                <Grid
                                    size={{
                                        sm: 6,
                                        xs: 12
                                    }}
                                >
                                    <Grid container spacing={1}>
                                        <Grid size={12}>
                                            <SwitchFormField name="pre_order" label="Preorders" />
                                        </Grid>
                                        <Grid size={12}>
                                            <SwitchFormField name="dine_in" label="Dine in" />
                                        </Grid>
                                        <Grid size={12}>
                                            <SwitchFormField name="tab" label="Bar Tabs" />
                                        </Grid>
                                        <Grid size={12}>
                                            <SwitchFormField name="order_to_table" label="Order to Table" />
                                        </Grid>
                                        <Grid size={12}>
                                            <SwitchFormField
                                                name="order_to_table_nfc"
                                                label="Order to Table NFCs Tags"
                                                description={
                                                    'This enables users to use their (NFC enabled) ' +
                                                    'phone to scan a tag, letting the app know what ' +
                                                    'table they want to open an order at.'
                                                }
                                            />
                                        </Grid>
                                        <Grid size={12}>
                                            <SwitchFormField name="pay_at_table" label="Pay at Table" />
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid
                                    size={{
                                        sm: 6,
                                        xs: 12
                                    }}
                                >
                                    <Grid container spacing={1}>
                                        <Grid size={12}>
                                            <SwitchFormField name="checkins" label="Checkins" />
                                        </Grid>
                                        <Grid size={12}>
                                            <SwitchFormField
                                                name="offline_checkin"
                                                label="Barcode Checkins & Rewards"
                                                description={
                                                    'This enables users to checkin and use rewards ' +
                                                    "using a barcode. This is useful when the location doesn't " +
                                                    'have a good internet connection.'
                                                }
                                            />
                                        </Grid>
                                        <Grid size={12}>
                                            <SwitchFormField name="book_table" label="Table Bookings" />
                                        </Grid>
                                        <Grid size={12}>
                                            <SwitchFormField name="book_room" label="Room Bookings" />
                                        </Grid>
                                        <Grid size={12}>
                                            <SwitchFormField
                                                name="pay_at_table_nfc"
                                                label="Pay at Table NFCs Tags"
                                                description={
                                                    'This enables users to use their (NFC enabled) ' +
                                                    'phone to scan a tag, letting the app know what ' +
                                                    'table they want to open an order at.'
                                                }
                                            />
                                        </Grid>
                                        {computedSettings.deliveryEnabled && (
                                            <Grid size={12}>
                                                <SwitchFormField name="delivery" label="Delivery" />
                                            </Grid>
                                        )}
                                    </Grid>
                                </Grid>
                                <Grid size={12}>
                                    <Row gutter align="flex-end">
                                        <Button onClick={toggleState} variant="outlined">
                                            Cancel
                                        </Button>
                                        <Button
                                            onClick={submitForm}
                                            disabled={isSubmitting}
                                            variant="contained"
                                            color="primary"
                                        >
                                            Submit
                                        </Button>
                                    </Row>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </MuiForm>
            ) : (
                <LocationServicesCardComponent services={currentLocation.services} />
            )}
        </Panel>
    );
};
